import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'boutique' | 'boutique-outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, disabled, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

    // Brand-carrying variants use the sage ramp — the ONLY primary scale the
    // theme system re-derives per tenant (apply-theme.ts rewrites
    // --color-sage-*). The legacy --color-green-* ramp in index.css is frozen
    // at LSG's original palette on every site, so a green primary button
    // shipped LSG's colour to navy, orange and plum tenants alike.
    const variants = {
      primary: 'bg-sage-600 text-white hover:bg-sage-700 active:bg-sage-800',
      secondary: 'bg-brown-100 text-brown-800 hover:bg-brown-200 active:bg-brown-300',
      outline: 'border-2 border-sage-600 text-sage-700 hover:bg-sage-50 active:bg-sage-100',
      ghost: 'text-ink-soft hover:bg-surface-muted active:bg-surface-muted',
      danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
      boutique: 'bg-cta text-cta-fg hover:bg-cta-deep active:bg-cta-deep',
      'boutique-outline': 'border-2 border-sage-600 text-sage-700 hover:bg-sage-50 active:bg-sage-100',
    };

    const sizes = {
      sm: 'h-8 px-3 text-sm gap-1.5',
      md: 'h-10 px-4 text-sm gap-2',
      lg: 'h-12 px-6 text-base gap-2.5',
      xl: 'h-14 px-8 text-base gap-3',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
